import { PaletteColor, Theme, createTheme } from "@mui/material";
import { useThemeMode } from "hooks/useThemeMode";
import { useMemo } from "react";
import { brandColors } from "./util";

// Add custom properties to the theme to be used throughout the app.
//
//  For example, theme.palette.grey50 will provide a light grey for light mode
// and a dark grey for dark mode.
declare module "@mui/material/styles" {
  interface Palette {
    /** Encamp's grey.50, which will also provide a grey for dark mode */
    grey50: PaletteColor;
  }
  // allow configuration using `createTheme()`
  interface PaletteOptions {
    /** Encamp's grey.50, which will also provide a grey for dark mode */
    grey50: PaletteColor;
  }
}

export function useEncampTheme(): Theme {
  const { themeMode } = useThemeMode();
  return useMemo(() => {
    const baseTheme = createTheme({
      components: {
        // https://github.com/mui/material-ui/issues/13253#issuecomment-752302891
        MuiDialog: {
          styleOverrides: {
            paper: {
              "& > form": {
                display: "contents",
              },
            },
          },
        },

        MuiDialogActions: {
          styleOverrides: {
            root: ({ theme }) => ({
              paddingTop: theme.spacing(3),
              paddingRight: theme.spacing(3),
              paddingBottom: theme.spacing(2),
              paddingLeft: theme.spacing(3),
            }),
          },
        },

        MuiDialogTitle: {
          styleOverrides: {
            root: ({ theme }) => ({
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(1.5),
            }),
          },
        },

        MuiDialogContent: {
          styleOverrides: {
            root: ({ theme }) => ({
              paddingBottom: theme.spacing(0),
            }),
          },
        },

        MuiButton: {
          styleOverrides: {
            root: {
              whiteSpace: "nowrap", // Prevent text from wrapping in all buttons
            },
          },
        },

        MuiToggleButton: {
          styleOverrides: {
            root: {
              whiteSpace: "nowrap",
            },
          },
        },

        MuiInputLabel: {
          styleOverrides: {
            root: {
              "& .input-tooltip": {
                pointerEvents: "auto", // Enables pointer events when the tooltip is inside the input
              },
            },
          },
        },

        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              "& a, & a:visited": {
                color: "#ADD8E6",
                textDecoration: "none",
              },
            },
          },
        },
      },

      palette: {
        mode: themeMode === "dark" ? "dark" : "light",
        text: {
          primary:
            themeMode !== "dark"
              ? brandColors["encamp-gray"][800]
              : brandColors["encamp-gray"][50],
          secondary: brandColors["encamp-gray"][500],
        },
        secondary: {
          main: brandColors["encamp-orange"][500],
          light: brandColors["encamp-orange"][200],
          dark: brandColors["encamp-orange"][700],
          contrastText: brandColors["neutral-gray"][50],
        },
        grey50:
          themeMode === "dark"
            ? {
                main: brandColors["encamp-gray"][700],
                light: brandColors["encamp-gray"][600],
                dark: brandColors["encamp-gray"][800],
                contrastText: brandColors["neutral-gray"][50],
              }
            : {
                main: brandColors["encamp-gray"][50],
                light: brandColors["encamp-gray"][50],
                dark: brandColors["encamp-gray"][100],
                contrastText: brandColors["neutral-gray"][900],
              },
      },
    });

    return baseTheme;
  }, [themeMode]);
}
